import {
  ChevronRight as NextPageIcon,
  ChevronLeft as PreviousPageIcon,
} from "@mui/icons-material";
import {
  TableContainer,
  Table,
  TableHead,
  TableBody,
  Stack,
  Typography,
  Select,
  MenuItem,
  useTheme,
} from "@mui/material";
import IconButton from "components/common/IconButton";
import { useMemo, useState } from "react";

const PaginatedTable = ({
  tableHead,
  tableRow,
  rows,
  pageSizeOptions,
  initialPaginationSize,
  size,
  sx,
}) => {
  const theme = useTheme();
  const [page, setPage] = useState(0);
  const [paginationSize, setPaginationSize] = useState(initialPaginationSize);
  const [sort, setSort] = useState(null);
  const borderColor = theme.brand.gray.gainsboro;

  const sortedRows = useMemo(() => {
    if (!sort) {
      return rows;
    }
    const sortAscending = sort.startsWith("-");
    const sortColumn = sort.replace(/^-/, "");
    const sorted = [...(rows ?? [])];

    const order = sortAscending ? 1 : -1;
    return sorted.sort((a, b) => {
      if (a[sortColumn] > b[sortColumn]) {
        return order;
      } else if (a[sortColumn] < b[sortColumn]) {
        return -order;
      } else {
        return 0;
      }
    });
  }, [rows, sort]);

  return (
    <Stack
      direction={"column"}
      sx={{
        paddingTop: 1,
        paddingBottom: 2,
        border: `1px solid ${borderColor}`,
        borderRadius: "5px",
      }}
    >
      <TableContainer style={{ ...sx }}>
        <Table
          size={size}
          stickyHeader
          sx={{ tableLayout: "fixed", width: "100%" }}
        >
          <TableHead>{tableHead({ sort, setSort })}</TableHead>
          <TableBody>
            {(
              sortedRows.slice(
                page * paginationSize,
                (page + 1) * paginationSize
              ) || []
            ).map((row) => tableRow(row))}
          </TableBody>
        </Table>
      </TableContainer>
      <Stack
        direction={"row"}
        justifyContent={"flex-end"}
        spacing={2}
        sx={{ paddingTop: 2, borderTop: `1px solid ${borderColor}` }}
      >
        <Stack direction={"row"} alignItems={"center"} spacing={1}>
          <Typography>{"Rows per page:"}</Typography>
          <Select
            value={paginationSize}
            onChange={(e) => {
              setPaginationSize(e.target.value);
              setPage((currentPage) =>
                Math.min(
                  Math.ceil(rows.length / e.target.value) - 1,
                  currentPage
                )
              );
            }}
            variant={"standard"}
            size={"small"}
            sx={{ height: 30, paddingTop: 1 }}
          >
            {(pageSizeOptions || []).map((option) => (
              <MenuItem value={option} key={option}>
                {option}
              </MenuItem>
            ))}
          </Select>
        </Stack>
        <Stack direction={"row"} alignItems={"center"} spacing={1}>
          <Typography>
            {`${page * paginationSize + 1}-${Math.min(
              (page + 1) * paginationSize,
              rows?.length
            )} of ${rows?.length || 0}`}
          </Typography>
          <IconButton
            scale={"small"}
            icon={PreviousPageIcon}
            edge={"start"}
            onClick={() => {
              setPage((currentPage) => currentPage - 1);
            }}
            disabled={page === 0}
          />
          <IconButton
            scale={"small"}
            icon={NextPageIcon}
            edge={"start"}
            onClick={() => {
              setPage((currentPage) => currentPage + 1);
            }}
            disabled={page === Math.ceil(rows.length / paginationSize) - 1}
          />
        </Stack>
      </Stack>
    </Stack>
  );
};

export default PaginatedTable;
